import { PageHeader, PageShell } from '@/components/PageShell';
import { RecallTopicBadge } from '@/components/badges/RecallTopicBadge';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
    approvedTopicRecallsState,
    awaitingTopicRecallsState,
    currentUserIdState,
    recallTopicCountsState,
    recallTopicsState,
    TOPIC_RECALLS_LIMIT,
} from '@/state';
import { useAsyncAtomValue, useLoadableAtomValue } from '@/state/loadableHooks';
import { formatDate } from '@/util/dates';
import logger from '@/util/logger';
import { RecallChangedError } from '@rock/dao';
import { RecallDao } from '@rock/db-admin';
import {
    planTopicsEdit,
    RECALL_TOPIC_FOLLOW_MAX_YEARS,
    type TopicApprovalBlock,
    topicApprovalBlock,
} from '@rock/recalls';
import {
    type Recall,
    type RecallId,
    recallKey,
    type RecallTopic,
    type RecallTopicId,
    type Timestamp,
    type UserId,
    UserIdSchema,
} from '@rock/types';
import { useAtomValue, useSetAtom } from 'jotai';
import { CircleCheck, CircleX } from 'lucide-react';
import { type FC, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { RecallRows } from './RecallsTable';
import { RECALL_STATUS_META } from './recallStatus';

const log = logger('RecallTopicPage');
const recallDao = new RecallDao();

/** Why a row can't be approved here, as the note under its title and the hover on its disabled check. */
const BLOCK_NOTES: Record<TopicApprovalBlock, string> = {
    'has-model-rules': 'Has model rules: approve on its page',
    'awaiting-rules': 'Typed, waiting for its rules: approve on its page',
    'no-topics': 'No topics',
    'not-awaiting': 'Not awaiting approval',
};

/**
 * One FYI topic: the recalls the classifier or an admin filed under it that await approval, approved in bulk
 * or one at a time, then the ones already live. Approving here only ever makes a recall live through its topics; a recall with
 * rules to review is approved on its own page.
 */
export const RecallTopicPage: FC = () => {
    const { topicId = '' } = useParams<{ topicId: string }>();
    const topics = useAsyncAtomValue(recallTopicsState);
    const topic = topics?.find((t) => t.id === topicId);

    return (
        <PageShell>
            <PageHeader
                title={topic?.title ?? (topics ? `No topic “${topicId}”` : 'Loading topic…')}
                description={topic?.description}
                id={topicId}
                backLabel="Back to Topics"
            />
            {topic && topics && (
                <>
                    <TopicQueueCard key={topic.id} topic={topic} topics={topics} />
                    <TopicLiveCard topic={topic} />
                </>
            )}
        </PageShell>
    );
};

/**
 * One topic's recalls awaiting approval. Approve All approves every one that can go live through its topics,
 * one at a time, and a row's own check approves just that one; a recall that doesn't belong is taken off the
 * topic (it leaves the list, and a reclassify won't put it back). A row that changed since it loaded, or
 * failed, stays with its reason.
 */
const TopicQueueCard: FC<{ topic: RecallTopic; topics: RecallTopic[] }> = ({ topic, topics }) => {
    const [recalls, loadable] = useLoadableAtomValue(awaitingTopicRecallsState(topic.id));
    const rawUserId = useAtomValue(currentUserIdState);
    const userId = useMemo(() => (rawUserId ? UserIdSchema.parse(rawUserId) : undefined), [rawUserId]);
    const recountTopics = useSetAtom(recallTopicCountsState);
    const topicTitles = useMemo(() => new Map(topics.map((t) => [t.id, t.title])), [topics]);
    const [problems, setProblems] = useState<ReadonlyMap<RecallId, string>>(new Map());
    const [pending, setPending] = useState<ReadonlySet<RecallId>>(new Set());
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);

    const rows = recalls ?? [];
    const approvable = rows.filter((recall) => !topicApprovalBlock(recall));

    const approveAll = async (user: UserId) => {
        setBusy(true);
        const outcome = await approveEach(approvable, user, topic.id);
        setBusy(false);
        setConfirming(false);
        setProblems(outcome.problems);
        recountTopics();
        if (outcome.problems.size === 0) {
            toast.success(`${outcome.approved} recall(s) approved`);
            return;
        }
        toast.warning(`${outcome.approved} approved, ${outcome.problems.size} not`, {
            description: 'Each one left behind says why beside it.',
        });
    };

    const approveOne = async (recall: Recall, user: UserId) => {
        setRowPending(recall.id, true);
        const outcome = await approveEach([recall], user, topic.id);
        setRowPending(recall.id, false);
        const problem = outcome.problems.get(recall.id);
        setRowProblem(recall.id, problem);
        if (problem) return;
        recountTopics();
        toast.success('Approved');
    };

    const removeTopic = async (recall: Recall) => {
        setRowPending(recall.id, true);
        const problem = await removeTopicFrom(recall, topic.id);
        setRowPending(recall.id, false);
        setRowProblem(recall.id, problem);
        if (problem) return;
        recountTopics();
        toast.success(`Removed from ${topic.title}`);
    };

    const setRowPending = (recallId: RecallId, isPending: boolean) =>
        setPending((current) => {
            const next = new Set(current);
            if (isPending) next.add(recallId);
            else next.delete(recallId);
            return next;
        });

    const setRowProblem = (recallId: RecallId, problem: string | undefined) =>
        setProblems((current) => {
            const next = new Map(current);
            if (problem) next.set(recallId, problem);
            else next.delete(recallId);
            return next;
        });

    return (
        <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div className="flex flex-col gap-1.5">
                    <CardTitle>{topic.title}: awaiting approval</CardTitle>
                    <CardDescription>
                        Classified or drafted recalls filed under this topic, newest first. Say no to any that don’t
                        belong (no reclassify files them here again), then approve the rest.
                        {rows.length >= TOPIC_RECALLS_LIMIT ? ` Showing the newest ${TOPIC_RECALLS_LIMIT}.` : ''}
                    </CardDescription>
                </div>
                <Button
                    onClick={() => setConfirming(true)}
                    disabled={!userId || busy || pending.size > 0 || approvable.length === 0}
                >
                    {busy ? 'Approving…' : `Approve All (${approvable.length})`}
                </Button>
            </CardHeader>
            <CardContent>
                {loadable.state === 'loading' ? (
                    <div className="section-description">Loading recalls…</div>
                ) : loadable.state === 'hasError' ? (
                    <div className="section-description">Couldn’t load recalls.</div>
                ) : rows.length === 0 ? (
                    <div className="section-description">Nothing awaiting approval under this topic.</div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[110px]">Published</TableHead>
                                <TableHead className="w-[110px]">Recall</TableHead>
                                <TableHead>Title</TableHead>
                                <TableHead>Kind</TableHead>
                                <TableHead>Also under</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="w-20" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((recall) => (
                                <QueueRow
                                    key={recall.id}
                                    recall={recall}
                                    topic={topic}
                                    topicTitles={topicTitles}
                                    disabled={busy || pending.has(recall.id)}
                                    problem={problems.get(recall.id)}
                                    onApprove={userId ? () => void approveOne(recall, userId) : undefined}
                                    onRemoveTopic={() => void removeTopic(recall)}
                                />
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
            <AlertDialog open={confirming} onOpenChange={(open) => !busy && setConfirming(open)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Approve {approvable.length} recall(s)?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Each goes live through its topics: every home following {topic.title}, or another of its
                            topics, gets it as an FYI if it was published since that home's follow started (a follow
                            reaches back up to {RECALL_TOPIC_FOLLOW_MAX_YEARS} years). Nobody is notified.
                            {approvable.length < rows.length
                                ? ` The ${rows.length - approvable.length} with rules to review stay for their own pages.`
                                : ''}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={busy || !userId}
                            onClick={(event) => {
                                event.preventDefault();
                                if (userId) void approveAll(userId);
                            }}
                        >
                            {busy ? 'Approving…' : 'Approve All'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Card>
    );
};

const QueueRow: FC<{
    recall: Recall;
    topic: RecallTopic;
    topicTitles: Map<RecallTopicId, string>;
    disabled: boolean;
    problem: string | undefined;
    onApprove: (() => void) | undefined;
    onRemoveTopic: () => void;
}> = ({ recall, topic, topicTitles, disabled, problem, onApprove, onRemoveTopic }) => {
    const block = topicApprovalBlock(recall);
    const meta = RECALL_STATUS_META[recall.status];
    const others = recall.classification?.topicIds.filter((id) => id !== topic.id) ?? [];
    return (
        <TableRow>
            <TableCell>{formatDate(recall.publishedAt)}</TableCell>
            <TableCell className="font-mono text-xs">{recallKey(recall.source, recall.sourceId)}</TableCell>
            <TableCell className="max-w-md whitespace-normal">
                <div className="flex flex-col gap-0.5">
                    <Link
                        to={`/products/recalls/${encodeURIComponent(recall.id)}`}
                        className="font-medium hover:underline"
                    >
                        {recall.title}
                    </Link>
                    {recall.classification?.headline && (
                        <span className="text-xs text-muted-foreground">{recall.classification.headline}</span>
                    )}
                    {(problem || block) && (
                        <span className={cn('text-xs', problem ? 'text-destructive' : 'text-muted-foreground')}>
                            {problem ?? (block ? BLOCK_NOTES[block] : '')}
                        </span>
                    )}
                </div>
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">
                {recall.classification?.productKind ?? '—'}
                {recall.typed ? ' · typed' : ''}
            </TableCell>
            <TableCell>
                {others.length ? (
                    <span className="flex flex-wrap gap-1">
                        {others.map((id) => (
                            <RecallTopicBadge key={id} topicId={id} title={topicTitles.get(id)} />
                        ))}
                    </span>
                ) : (
                    <span className="text-muted-foreground">—</span>
                )}
            </TableCell>
            <TableCell>
                <Badge variant={meta.variant} className={meta.className}>
                    {meta.label}
                </Badge>
            </TableCell>
            <TableCell>
                <span className="flex items-center gap-1">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="inline-flex">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-emerald-600 hover:text-emerald-700"
                                    aria-label={`Approve ${recall.title}`}
                                    disabled={disabled || !onApprove || !!block}
                                    onClick={onApprove}
                                >
                                    <CircleCheck className="h-4 w-4" />
                                </Button>
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>
                            {block ? BLOCK_NOTES[block] : 'Approve: live through its topics, nobody notified'}
                        </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-destructive hover:text-destructive"
                                aria-label={`${recall.title} doesn’t belong under ${topic.title}`}
                                disabled={disabled}
                                onClick={onRemoveTopic}
                            >
                                <CircleX className="h-4 w-4" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>No, this doesn’t apply: take it off {topic.title} for good</TooltipContent>
                    </Tooltip>
                </span>
            </TableCell>
        </TableRow>
    );
};

/** One topic's live recalls, newest first; a following home holds those published since its follow started. */
const TopicLiveCard: FC<{ topic: RecallTopic }> = ({ topic }) => {
    const [recalls, loadable] = useLoadableAtomValue(approvedTopicRecallsState(topic.id));
    const rows = recalls ?? [];
    return (
        <Card>
            <CardHeader>
                <CardTitle>{topic.title}: live</CardTitle>
                <CardDescription>
                    Approved recalls under this topic, newest first. Reopen one on its page to take it back.
                    {rows.length >= TOPIC_RECALLS_LIMIT ? ` Showing the newest ${TOPIC_RECALLS_LIMIT}.` : ''}
                </CardDescription>
            </CardHeader>
            <CardContent>
                {loadable.state === 'loading' ? (
                    <div className="section-description">Loading recalls…</div>
                ) : loadable.state === 'hasError' ? (
                    <div className="section-description">Couldn’t load recalls.</div>
                ) : rows.length === 0 ? (
                    <div className="section-description">No live recalls under this topic yet.</div>
                ) : (
                    <RecallRows recalls={rows} />
                )}
            </CardContent>
        </Card>
    );
};

/** Approve each recall in turn; what didn't go through comes back keyed by recall, with why. */
async function approveEach(
    recalls: Recall[],
    reviewedBy: UserId,
    topicId: RecallTopicId,
): Promise<{ approved: number; problems: Map<RecallId, string> }> {
    const now = new Date().toISOString() as Timestamp;
    const problems = new Map<RecallId, string>();
    let approved = 0;
    for (const recall of recalls) {
        try {
            await recallDao.approve(recall, reviewedBy, now);
            approved++;
        } catch (err) {
            if (err instanceof RecallChangedError) {
                problems.set(recall.id, 'Changed since it loaded: check it and approve again');
                continue;
            }
            log.error('Failed to approve a recall from its topic', { err, recallId: recall.id, topicId });
            problems.set(recall.id, `Couldn’t approve: ${err instanceof Error ? err.message : 'unexpected error'}`);
        }
    }
    return { approved, problems };
}

/**
 * Take one topic off a recall, "no, this doesn't apply": ruled out, so no reclassify files it there again.
 * Undefined when it went through, else why not, for its row.
 */
async function removeTopicFrom(recall: Recall, topicId: RecallTopicId): Promise<string | undefined> {
    const { classification } = recall;
    if (!classification) return 'Not classified yet';
    const plan = planTopicsEdit(
        classification,
        classification.topicIds.filter((id) => id !== topicId),
    );
    try {
        await recallDao.setTopics(recall, plan, new Date().toISOString() as Timestamp);
        return undefined;
    } catch (err) {
        if (err instanceof RecallChangedError) return 'Changed since it loaded: check it and try again';
        log.error('Failed to remove a recall from its topic', { err, recallId: recall.id, topicId });
        return `Couldn’t remove the topic: ${err instanceof Error ? err.message : 'unexpected error'}`;
    }
}
